<script setup lang="ts">
import Button from '~/components/App/Button/index.vue'
import Icon from '~/components/Icon/Legacy/index.vue'

interface IProps {
  title: string
  emphasisText?: string
  subtitle?: string
  colorBackground?: string
  imagePosition?: 'left' | 'right'
  primaryButtonText?: string
  primaryButtonUrl?: string
  primaryButtonBackgroundColor?: string
  primaryButtonIconPosition?: 'left' | 'right'
  titleCheck?: string
  textCheck?: string
}

withDefaults(
  defineProps<IProps>(),
  {
    title: 'Create, Share, & Discover Prompts',
    subtitle: 'Effortlessly create and discover prompts to boost productivity, making AI utilization straightforward and engaging.',
    imagePosition: 'right',
    primaryButtonText: 'Install our ChatGPT Plugin',
    primaryButtonIconPosition: 'right',
    primaryButtonBackgroundColor: 'black',
    titleCheck: '',
    textCheck: '',
  },
)

const emit = defineEmits<IEmits>()

interface IEmits {
  (e: 'handleClick'): void
}

const { colors } = useTheme()

const emitCustomEvent = () => {
  emit('handleClick')
}
</script>

<template>
  <div
    class="wrapper"
    w-full relative flex gap-7 p-7 p-28px
    border="<md:1 md:bluish-softer" rounded-2
    :style="{ backgroundColor: colorBackground || colors.white }"
  >
    <div class="wrapper-container-left" :class="{ [imagePosition]: true }">
      <div v-if="emphasisText || title || subtitle || primaryButtonText" class="text-wrapper" w-full flex col gap-7 max-w-240px>
        <div class="title">
          <div relative flex items-center>
            <slot name="icon" />
          </div>

          <h6 v-if="title" leading-none text="20px start" max-w="400px lg:440px" :style="{ fontFeatureSettings: '\'ss02\' on, \'ss01\' on,\'salt\'on', letterSpacing: '-0.64px' }">
            {{ title }}
            <span v-if="emphasisText" leading-none text="20px start" :style="{ background: 'linear-gradient(250deg, #35DBFF -2.27%, #B09EF8 43.11%, #FD93E7 100.75%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFeatureSettings: '\'ss02\' on, \'ss01\' on,\'salt\'on', letterSpacing: '-0.56px' }">
              {{ emphasisText }}
            </span>
          </h6>
        </div>

        <!-- subtitle -->
        <p v-if="subtitle" font-inter text="left 12px" :style="{ lineHeight: '175%', color: colors.darkerGray, fontVariantNumeric: 'lining-nums proportional-nums', fontFeatureSettings: '\'cv09\' on, \'cv01\' on' }">
          {{ subtitle }}
        </p>

        <!-- primary button -->
        <NuxtLink v-if="primaryButtonText && primaryButtonUrl" :to="primaryButtonUrl" target="_blank" w-full mt-auto>
          <Button
            class="btn-primary" variant="primary" :background-color="primaryButtonBackgroundColor" :icon-position="primaryButtonIconPosition" :button-style="{
              padding: '8px 0',
              height: '32px',
              gap: '6px',
              width: '100%',
            }"
            :text-style="{
              fontSize: '14px',
              fontFamily: 'Circular Std Book',
              whiteSpace: 'nowrap',
              lineHeight: '160%',
              fontFeatureSettings: '\'ss02\' on, \'ss01\' on,\'salt\'on',
              letterSpacing: '-0.56px',
            }"
            @click="emitCustomEvent"
          >
            {{ primaryButtonText }}
            <template #icon>
              <slot name="primaryButtonIcon" />
            </template>
          </Button>
        </NuxtLink>

        <NuxtLink v-if="primaryButtonUrl" :to="primaryButtonUrl" class="btn-secondary">
          Read More
          <Icon name="arrow-thin" />
        </NuxtLink>
      </div>

      <div class="wrapper-container-right">
        <slot name="image" />

        <div v-if="titleCheck || textCheck" class="text-check">
          <!-- <div class="i-mdi:check" w-12px h-12px mt-4px /> -->
          <Icon name="check-thin" w-12px h-12px mt-4px fill-black />

          <div col justify-start items-start text-left>
            <p font-inter text-12px :color="colors.softBlack" :style="{ lineHeight: '175%', fontFeatureSettings: '\'cv09\' on, \'cv01\' on', fontWeight: '700' }">
              {{ titleCheck }}
            </p>
            <p font-inter text-11px :color="colors.darkerGray" :style="{ opacity: '0.6', lineHeight: '175%', fontVariantNumeric: 'lining-nums proportional-nums', fontFeatureSettings: '\'cv09\' on, \'cv01\' on', fontWeight: '400' }">
              {{ textCheck }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  .wrapper-container-left {
    display: flex;
    gap: 40px;

    &.left {
      flex-direction: row-reverse;
      margin: 0 0 0 auto;
    }

    .title {
      display: flex;
      flex-direction: column;
      gap: 22px;
    }

    .btn-secondary {
      --at-apply: text-bluish-darker;
      display: flex;
      align-items: center;
      gap: 5px;
      font-family: 'Circular Std Bold';
      font-size: 14px;
      font-weight: 700;
      line-height: 160%;
      letter-spacing: -0.48px;
      font-feature-settings: 'ss02' on, 'ss01' on,'salt' on;
      display: none;

      svg {
        --at-apply: fill-bluish-darker;
        width: 11px;
        height: 11px;
        transform: rotate(-90deg);
      }
    }
  }

  .wrapper-container-right {
    display: flex;
    flex-direction: column;

    .text-check {
      // flex items-start mt-auto gap-1
      display: flex;
      margin: 22px 0 0 0;
      gap: 6px;
    }
  }
}
@media screen and (max-width: 1200px) {
  .wrapper {
    --at-apply: bg-white;
    flex-direction: column-reverse;
    padding: 0;
    .wrapper-container-left, .wrapper-container-left.left {
      width: 100%;
      flex-direction: column-reverse;
      gap: 20px;
      .title {
        flex-direction: row-reverse;

        h6 {
          font-size: 16px;
          margin: 0 auto 0 0;
        }
      }

      .text-wrapper {
        padding: 0 20px 20px 20px;
        max-width: unset;
      }

      .btn-primary {
        display: none;
      }

      .btn-secondary {
        display: flex;
        margin: 0 0 0 auto;
      }

      .wrapper-container-right {
        .text-check {
          display: none;
        }
      }
    }
  }
}
</style>
